// src/components/tip_bon_iesire/TipBonIesireEdit.jsx
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  Spinner,
  Alert,
} from "react-bootstrap";
import AccountPickerModal from "../pickers/AccountPickerModal";
import { authFetch } from "../utils/authFetch";

export default function TipBonIesireEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [showContD, setShowContD] = useState(false);
  const [showContC, setShowContC] = useState(false);
  const [form, setForm] = useState(null);

  const [optContD, setOptContD] = useState(null);
  const [optContC, setOptContC] = useState(null);

  const makeAccountLabel = useCallback((c) => {
    const nr =
      c.cont ?? c.number ?? c.numar ?? c.account ?? c.code ?? c.cod ?? "";
    const den = c.denumire ?? c.name ?? c.descriere ?? c.title ?? "";
    return [nr, den].filter(Boolean).join(" — ") || `Account #${c.id}`;
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await authFetch(`/tip_bon_iesire/${id}`);

        setForm({
          denumire: data.denumire ?? "",
          contd_id: data.contd_id ?? null,
          contc_id: data.contc_id ?? null,
        });

        const preloadAccount = async (accId, setter) => {
          if (!accId) {
            setter(null);
            return;
          }
          try {
            const a = await authFetch(`/accounts/${accId}`);
            setter(a);
          } catch {
            setter({ id: accId, code: "", denumire: `Account #${accId}` });
          }
        };

        await Promise.all([
          preloadAccount(data.contd_id, setOptContD),
          preloadAccount(data.contc_id, setOptContC),
        ]);
      } catch (e) {
        setErr(String(e?.message || e));
      } finally {
        setLoading(false);
      }
    })();
  }, [id, makeAccountLabel]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({
      ...f,
      [name]:
        type === "checkbox"
          ? checked
          : type === "number"
            ? value === ""
              ? ""
              : Number(value)
            : value,
    }));
  };

  const onChangeContD = (opt) => {
    setOptContD(opt);
    setForm((f) => ({ ...f, contd_id: opt ? Number(opt.value) : null }));
  };
  const onChangeContC = (opt) => {
    setOptContC(opt);
    setForm((f) => ({ ...f, contc_id: opt ? Number(opt.value) : null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);

      if (!form.denumire?.trim()) throw new Error("Denumire obligatorie.");
      if (!form.contd_id) throw new Error("Cont debit (contd_id) obligatoriu.");

      const payload = {
        denumire: form.denumire.trim(),
        contd_id: Number(form.contd_id),
        contc_id: form.contc_id ? Number(form.contc_id) : null,
      };

      await authFetch(`/tip_bon_iesire/${id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });

      navigate("/tip-bon-iesire/");
    } catch (e) {
      setErr("Eroare la salvare: " + (e?.message ?? "unknown"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container className="mt-4">
        <Spinner animation="border" size="sm" /> Se încarcă…
      </Container>
    );
  }

  if (!form) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          {err || "Nu s-a putut încărca înregistrarea."}
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Card className="p-3">
        <h3>Editează TipBonIesire #{id}</h3>
        {err && (
          <Alert variant="danger" className="mt-2">
            {err}
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Denumire</Form.Label>
                <Form.Control
                  type="text"
                  name="denumire"
                  value={form.denumire ?? ""}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Cont debit (contd_id)</Form.Label>

                <div
                  className="form-control d-flex align-items-center justify-content-between"
                  style={{ cursor: "pointer" }}
                  role="button"
                  tabIndex={0}
                  onClick={() => setShowContD(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setShowContD(true);
                    }
                  }}
                >
                  <span className={optContD ? "" : "text-muted"}>
                    {optContD
                      ? `${optContD.code ?? optContD.cod ?? ""} • ${
                          optContD.denumire ?? optContD.name ?? ""
                        }`
                      : "Click pentru a alege cont debit…"}
                  </span>

                  {optContD ? (
                    <span
                      title="Șterge selecția"
                      style={{ padding: "0 6px" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setOptContD(null);
                        setForm((f) => ({ ...f, contd_id: null }));
                      }}
                    >
                      ✕
                    </span>
                  ) : (
                    <span className="text-muted">▾</span>
                  )}
                </div>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Cont credit default (contc_id) (opțional)
                </Form.Label>

                <div
                  className="form-control d-flex align-items-center justify-content-between"
                  style={{ cursor: "pointer" }}
                  role="button"
                  tabIndex={0}
                  onClick={() => setShowContC(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setShowContC(true);
                    }
                  }}
                >
                  <span className={optContC ? "" : "text-muted"}>
                    {optContC
                      ? `${optContC.code ?? optContC.cod ?? ""} • ${
                          optContC.denumire ?? optContC.name ?? ""
                        }`
                      : "Click pentru a alege cont credit…"}
                  </span>

                  {optContC ? (
                    <span
                      title="Șterge selecția"
                      style={{ padding: "0 6px" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setOptContC(null);
                        setForm((f) => ({ ...f, contc_id: null }));
                      }}
                    >
                      ✕
                    </span>
                  ) : (
                    <span className="text-muted">▾</span>
                  )}
                </div>
              </Form.Group>
            </Col>
          </Row>

          <div className="text-end">
            <Button type="submit" disabled={submitting}>
              {submitting ? "Se salvează…" : "Salvează"}
            </Button>
          </div>
        </Form>
      </Card>
      <AccountPickerModal
        show={showContD}
        onHide={() => setShowContD(false)}
        onSelect={(acc) => {
          setOptContD(acc);
          setForm((f) => ({ ...f, contd_id: acc.id }));
        }}
      />

      <AccountPickerModal
        show={showContC}
        onHide={() => setShowContC(false)}
        onSelect={(acc) => {
          setOptContC(acc);
          setForm((f) => ({ ...f, contc_id: acc.id }));
        }}
      />
    </Container>
  );
}
